import { Runtime, pipe } from "fp";
import React from "react";

import * as Log from "~/Log";
import * as OptionalNonEmptyString from "~/OptionalNonEmptyString";
import { Time } from "~/Time";

export type Signature = {
  readonly signatory: string;
  readonly dateTime: Time.DateTime;
};

export const useFromActiveLog = function <A>(
  route: Log.Route,
  {
    missing,
    found,
  }: {
    readonly missing: () => A;
    readonly found: (signature: Signature) => A;
  }
): A {
  return Log.Data.useActiveLogWhen(route, {
    unavailable: missing,
    available: ({ signature }) => (signature ? found(signature) : missing()),
  });
};

export const Component = (route: Log.Route) =>
  useFromActiveLog(route, {
    missing: () => null,
    found: (signature) => (
      <>
        Signed by {signature.signatory} on&nbsp;
        {Time.DateTime.toDateOnlyMDYY(signature.dateTime)} at&nbsp;
        {Time.DateTime.toHMM(signature.dateTime)}
      </>
    ),
  });

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      signed_by: OptionalNonEmptyString.Response.runtime(),
      signed_on: Runtime.nullable(Time.DateTime.Response.runtime()),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Signature | undefined =>
    pipe(
      OptionalNonEmptyString.Response.decode(response.signed_by),
      (signatory) =>
        signatory && response.signed_on
          ? { signatory, dateTime: response.signed_on }
          : undefined
    );
}
